import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, UserCircle } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import PlayerProfileFields from "@/components/PlayerProfileFields";
import FacebookLinkField from "@/components/FacebookLinkField";
import VerifiedBadge from "@/components/VerifiedBadge";
import { formatApiError } from "@/lib/api";

const inputCls = "w-full rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400";

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [profile, setProfile] = useState(user?.profile || {});
  const [busy, setBusy] = useState(false);
  const isPlayer = user?.account_type === "player";
  const nav = useNavigate();
  const [params] = useSearchParams();
  const back = params.get("return");

  const save = async () => {
    setBusy(true);
    try {
      await updateProfile({ name, profile });
      toast.success("Profile saved");
      if (back && back.startsWith("/") && !back.startsWith("//")) nav(back);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Profile" />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 grain" data-testid="profile-page">
        <div className="flex items-center gap-2 mb-6"><UserCircle className="w-6 h-6 text-primary-c" /><h1 className="font-display font-bold text-2xl flex items-center gap-1.5">Your profile<VerifiedBadge verified={user?.verified} size={20} /></h1>
          <span className="ml-2 text-[10px] mono uppercase tracking-wider glass rounded-full px-2 py-0.5" style={{ color: "var(--text-2)" }}>{user?.account_type || "organizer"}</span>
        </div>

        <div className="rounded-2xl glow-border p-5 space-y-4" style={{ background: "var(--surface)" }}>
          <div>
            <label className="text-xs uppercase font-bold tracking-wider" style={{ color: "var(--text-2)" }}>Full name</label>
            <input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className={`${inputCls} mt-1`} style={{ color: "var(--text)" }} />
          </div>
          <div>
            <label className="text-xs uppercase font-bold tracking-wider" style={{ color: "var(--text-2)" }}>Email</label>
            <input readOnly value={user?.email || ""} className={`${inputCls} mt-1 opacity-70`} style={{ color: "var(--text)" }} />
          </div>

          {!isPlayer && <p className="text-xs pt-2" style={{ color: "var(--text-2)" }}>Player details are optional for organizer accounts — they appear on your player profile and rating card.</p>}
          <div className="pt-2 border-t" style={{ borderColor: "var(--border)" }}><PlayerProfileFields value={profile} onChange={setProfile} /></div>

          <button data-testid="profile-save" onClick={save} disabled={busy} className="btn-glow rounded-full px-6 py-2.5 font-bold flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
          </button>
        </div>
      </div>
    </div>
  );
}
